import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Separator } from "@/components/ui/separator";
import {
  Activity,
  Users,
  Building2,
  DollarSign,
  TrendingUp,
  TrendingDown,
  Clock,
  AlertTriangle,
  CheckCircle,
  RefreshCw,
  Wifi,
  WifiOff,
  Eye,
  EyeOff,
} from "lucide-react";
import { toast } from "sonner";
import { apiService, AdminDashboardData } from "@/services/api";
import dayjs from "dayjs";

interface RealTimeMetric {
  id: string;
  name: string;
  value: number;
  change: number;
  changeType: "increase" | "decrease" | "stable";
  trend: "up" | "down" | "stable";
  lastUpdated: Date;
}

interface LiveActivity {
  id: string;
  type: "user_login" | "job_posted" | "payment_received" | "user_registered" | "system_alert";
  message: string;
  timestamp: Date;
  severity: "info" | "warning" | "error" | "success";
  userId?: string;
  details?: string;
}

interface SystemStatus {
  service: string;
  status: "online" | "offline" | "degraded";
  responseTime: number;
  lastCheck: Date;
}

export function RealTimeDashboard() {
  const [isLive, setIsLive] = useState(true);
  const [refreshInterval, setRefreshInterval] = useState(30000); // 30 seconds
  const [metrics, setMetrics] = useState<RealTimeMetric[]>([]);
  const [liveActivities, setLiveActivities] = useState<LiveActivity[]>([]);
  const [systemStatus, setSystemStatus] = useState<SystemStatus[]>([]);
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [dashboardData, setDashboardData] = useState<AdminDashboardData | null>(null);
  const [loading, setLoading] = useState(true);

  // Load dashboard data from API
  const loadDashboardData = async () => {
    try {
      setLoading(true);
      const response = await apiService.getAdminDashboard();
      if (response.type === 1) {
        setDashboardData(response.data);
        updateMetricsFromAPI(response.data);
        updateLiveActivitiesFromAPI(response.data);
      } else {
        toast.error(response.message || "Failed to load dashboard data");
      }
    } catch (error) {
      toast.error("Failed to load dashboard data");
      console.error("Error loading dashboard:", error);
    } finally {
      setLoading(false);
    }
  };

  // Load system health status
  const loadSystemHealth = async () => {
    try {
      const response = await apiService.getSystemHealth();
      if (response.type === 0) {
        const healthData = response.data;
        
        const services: SystemStatus[] = [
          {
            service: "API Server",
            status: healthData.server === "running" ? "online" : "offline",
            responseTime: healthData.responseTimes?.total || 0,
            lastCheck: new Date(healthData.timestamp),
          },
          {
            service: "MongoDB Database",
            status: healthData.mongodb === "connected" ? "online" : "offline",
            responseTime: healthData.responseTimes?.mongodb || 0,
            lastCheck: new Date(healthData.timestamp),
          },
        ];

        // Add Redis status if available
        if (healthData.redis) {
          services.push({
            service: "Redis Cache",
            status: healthData.redis === "connected" ? "online" : "offline",
            responseTime: healthData.responseTimes?.redis || 0,
            lastCheck: new Date(healthData.timestamp),
          });
        }

        setSystemStatus(services);
      }
    } catch (error) {
      console.error("Error loading system health:", error);
      // Set services as offline on error
      setSystemStatus([
        {
          service: "API Server",
          status: "offline",
          responseTime: -1,
          lastCheck: new Date(),
        },
        {
          service: "MongoDB Database",
          status: "offline",
          responseTime: -1,
          lastCheck: new Date(),
        },
        {
          service: "Redis Cache",
          status: "offline",
          responseTime: -1,
          lastCheck: new Date(),
        },
      ]);
    }
  };

  // Update metrics from API data
  const updateMetricsFromAPI = (data: AdminDashboardData) => {
    setMetrics([
      {
        id: "1",
        name: "Total Users",
        value: data.overview.totalUsers,
        change: data.overview.userGrowth,
        changeType: data.overview.userGrowth > 0 ? "increase" : "decrease",
        trend: data.overview.userGrowth > 0 ? "up" : "down",
        lastUpdated: new Date(),
      },
      {
        id: "2",
        name: "Active Users",
        value: data.overview.activeUsers,
        change: data.overview.userGrowth,
        changeType: data.overview.userGrowth > 0 ? "increase" : "decrease",
        trend: data.overview.userGrowth > 0 ? "up" : "down",
        lastUpdated: new Date(),
      },
      {
        id: "3",
        name: "Establishments",
        value: data.overview.totalEstablishments,
        change: 0,
        changeType: "stable",
        trend: "stable",
        lastUpdated: new Date(),
      },
      {
        id: "4",
        name: "Total Revenue",
        value: data.overview.totalRevenue,
        change: data.overview.revenueGrowth,
        changeType: data.overview.revenueGrowth > 0 ? "increase" : "decrease",
        trend: data.overview.revenueGrowth > 0 ? "up" : "down",
        lastUpdated: new Date(),
      },
    ]);
  };

  // Update live activities from API data
  const updateLiveActivitiesFromAPI = (data: AdminDashboardData) => {
    if (!data.activities || data.activities.length === 0) {
      // Fallback to recent users if no activities
      const fallbackActivities: LiveActivity[] = data.recentActivity.recentUsers.map(
        (user, index) => ({
          id: `user-${index}`,
          type: "user_registered" as const,
          message: `${user.name} registered`,
          timestamp: new Date(user.createdAt),
          severity: "info" as const,
          userId: user.id,
          details: `Email: ${user.email}`,
        })
      );
      setLiveActivities(fallbackActivities.slice(0, 15));
      return;
    }

    const activities: LiveActivity[] = data.activities.map(activity => ({
      id: activity.id,
      type: activity.type,
      message: activity.message,
      timestamp: new Date(activity.timestamp),
      severity: activity.severity,
      userId: activity.userId,
      details: activity.details,
    }));

    setLiveActivities(activities.slice(0, 15)); // Show last 15 activities
  };

  // Real-time data updates
  useEffect(() => {
    if (!isLive) return;

    const interval = setInterval(() => {
      void loadDashboardData();
      void loadSystemHealth();
    }, refreshInterval);

    return () => {
      clearInterval(interval);
    };
  }, [isLive, refreshInterval]);

  // Initialize with real data
  useEffect(() => {
    void loadDashboardData();
    void loadSystemHealth();
  }, []);

  const handleManualRefresh = async () => {
    await loadDashboardData();
    await loadSystemHealth();
    setLastRefresh(new Date());
    toast.success("Dashboard refreshed");
  };

  const getActivityIcon = (type: LiveActivity["type"]) => {
    switch (type) {
      case "user_login":
        return <Users className="w-4 h-4" />;
      case "job_posted":
        return <Building2 className="w-4 h-4" />;
      case "payment_received":
        return <DollarSign className="w-4 h-4" />;
      case "user_registered":
        return <Users className="w-4 h-4" />;
      case "system_alert":
        return <AlertTriangle className="w-4 h-4" />;
      default:
        return <Activity className="w-4 h-4" />;
    }
  };

  const getSeverityColor = (severity: LiveActivity["severity"]) => {
    switch (severity) {
      case "success":
        return "text-green-600 bg-green-50";
      case "warning":
        return "text-yellow-600 bg-yellow-50";
      case "error":
        return "text-red-600 bg-red-50";
      default:
        return "text-blue-600 bg-blue-50";
    }
  };

  const getStatusColor = (status: SystemStatus["status"]) => {
    switch (status) {
      case "online":
        return "bg-green-600";
      case "offline":
        return "bg-red-600";
      case "degraded":
        return "bg-yellow-600";
      default:
        return "bg-gray-600";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <RefreshCw className="w-6 h-6 animate-spin mr-2" />
        Loading dashboard data...
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">Real-Time Dashboard</h2>
          <p className="text-muted-foreground">
            Live monitoring and instant updates from your platform
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Badge variant={isLive ? "default" : "secondary"} className="flex items-center space-x-1">
            {isLive ? <Wifi className="w-3 h-3" /> : <WifiOff className="w-3 h-3" />}
            {isLive ? "Live" : "Paused"}
          </Badge>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setIsLive(!isLive);
            }}
          >
            {isLive ? <EyeOff className="w-4 h-4 mr-2" /> : <Eye className="w-4 h-4 mr-2" />}
            {isLive ? "Pause" : "Resume"}
          </Button>
          <Button variant="outline" size="sm" onClick={handleManualRefresh}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </div>

      {/* Last Updated */}
      <div className="text-sm text-muted-foreground">
        Last updated: {lastRefresh.toLocaleTimeString()}
      </div>

      {/* Real-time Metrics */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
        {metrics.map(metric => (
          <Card key={metric.id} className="relative overflow-hidden">
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">{metric.name}</CardTitle>
              <div className="flex items-center space-x-1">
                {metric.trend === "up" && <TrendingUp className="w-4 h-4 text-green-600" />}
                {metric.trend === "down" && <TrendingDown className="w-4 h-4 text-red-600" />}
                {metric.trend === "stable" && <Clock className="w-4 h-4 text-gray-600" />}
              </div>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">
                {metric.name.includes("Revenue")
                  ? `$${metric.value.toLocaleString()}`
                  : metric.value.toLocaleString()}
              </div>
              <div className="flex items-center space-x-1 text-xs text-muted-foreground">
                <span
                  className={
                    metric.changeType === "increase"
                      ? "text-green-600"
                      : metric.changeType === "decrease"
                      ? "text-red-600"
                      : "text-gray-600"
                  }
                >
                  {metric.change > 0 ? "+" : ""}
                  {metric.change}%
                </span>
                <span>from last hour</span>
              </div>
            </CardContent>
            <div className="absolute top-0 right-0 w-2 h-full bg-gradient-to-b from-blue-500 to-purple-500 opacity-20" />
          </Card>
        ))}
      </div>

      {/* Tabs for different views */}
      <Tabs defaultValue="activities" className="space-y-4">
        <TabsList>
          <TabsTrigger value="activities">Live Activities</TabsTrigger>
          <TabsTrigger value="system">System Status</TabsTrigger>
          <TabsTrigger value="settings">Dashboard Settings</TabsTrigger>
        </TabsList>

        {/* Live Activities Tab */}
        <TabsContent value="activities" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <Activity className="w-5 h-5" />
                <span>Live Activities</span>
                <Badge variant="outline" className="ml-2">
                  {liveActivities.length} recent
                </Badge>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {liveActivities.map(activity => (
                  <div
                    key={activity.id}
                    className="flex items-start space-x-3 p-3 rounded-lg border"
                  >
                    <div className={`p-2 rounded-full ${getSeverityColor(activity.severity)}`}>
                      {getActivityIcon(activity.type)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium">{activity.message}</p>
                      {activity.details && (
                        <p className="text-xs text-muted-foreground mt-1">{activity.details}</p>
                      )}
                      <p className="text-xs text-muted-foreground mt-1">
                        {activity.timestamp.toLocaleTimeString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* System Status Tab */}
        <TabsContent value="system" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center space-x-2">
                <CheckCircle className="w-5 h-5" />
                <span>System Health</span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {systemStatus.map(service => (
                  <div
                    key={service.service}
                    className="flex items-center justify-between p-3 rounded-lg border"
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-3 h-3 rounded-full ${getStatusColor(service.status)}`} />
                      <span className="font-medium">{service.service}</span>
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                      <span className={service.responseTime === -1 ? "text-red-600" : ""}>
                        {service.responseTime === -1 ? "N/A" : `${service.responseTime}ms`}
                      </span>
                      <span>{service.lastCheck.toLocaleTimeString()}</span>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dashboard Settings Tab */}
        <TabsContent value="settings" className="space-y-4">
          <Card>
            <CardHeader>
              <CardTitle>Dashboard Configuration</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Refresh Interval</label>
                <select
                  className="w-full p-2 border rounded-md"
                  value={refreshInterval}
                  onChange={e => {
                    setRefreshInterval(Number(e.target.value));
                  }}
                >
                  <option value={1000}>1 second</option>
                  <option value={5000}>5 seconds</option>
                  <option value={10000}>10 seconds</option>
                  <option value={30000}>30 seconds</option>
                  <option value={60000}>1 minute</option>
                </select>
              </div>

              <Separator />

              <div className="space-y-2">
                <label className="text-sm font-medium">Auto-refresh</label>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="auto-refresh"
                    checked={isLive}
                    onChange={e => {
                      setIsLive(e.target.checked);
                    }}
                    className="rounded"
                  />
                  <label htmlFor="auto-refresh" className="text-sm">
                    Enable automatic updates
                  </label>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
